import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import classNames from "classnames";

import { ReactComponent as MicrophoneIcon } from "../../icons/microphone.svg";

import "./VoiceInput.scss";

interface ISpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface ISpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: ISpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: Event) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

declare global {
  interface Window {
    SpeechRecognition: new () => ISpeechRecognition;
    webkitSpeechRecognition: new () => ISpeechRecognition;
  }
}

export interface VoiceInputHandle {
  stopListening: () => void;
}

interface VoiceInputProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
}

const VoiceInput = forwardRef<VoiceInputHandle, VoiceInputProps>(
  function VoiceInput({ onTranscript, disabled = false }, ref) {
    const [isListening, setIsListening] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const recognitionRef = useRef<ISpeechRecognition | null>(null);

    useEffect(() => {
      const SpeechRecognitionAPI =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      setIsSupported(!!SpeechRecognitionAPI);
    }, []);

    const stopListening = () => {
      recognitionRef.current?.stop();
      setIsListening(false);
    };

    useImperativeHandle(ref, () => ({ stopListening }), []);

    const startListening = () => {
      const SpeechRecognitionAPI =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognitionAPI) return;

      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.lang = "en-US";

      recognition.onresult = (event: ISpeechRecognitionEvent) => {
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            transcript += event.results[i][0].transcript;
          }
        }
        if (transcript) {
          onTranscript(transcript);
        }
      };

      recognition.onend = () => setIsListening(false);
      recognition.onerror = () => setIsListening(false);

      recognitionRef.current = recognition;
      recognition.start();
      setIsListening(true);
    };

    const handleToggle = () => {
      if (isListening) {
        stopListening();
      } else {
        startListening();
      }
    };

    useEffect(() => {
      return () => {
        recognitionRef.current?.abort();
      };
    }, []);

    if (!isSupported) return null;

    return (
      <button
        className={classNames("voice-input__btn", {
          "voice-input__btn--listening": isListening,
        })}
        onClick={handleToggle}
        disabled={disabled}
        type="button"
        title={isListening ? "Stop recording" : "Voice input"}
      >
        <MicrophoneIcon />
      </button>
    );
  },
);

export default VoiceInput;
